// =============================================================================
// AGC: [CROP_REC] useCropRecommendations Hook
// AGC: Fetches geo + climate aware crop recommendations from backend API
// AGC: Uses lot geolocation and historical weather data for potentiality calculation
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from './useAuth'
import { getBackendUrl } from '../utils/backendUrl'
import type {
  CropRecommendation,
  ClimateProfile,
  RecommendationAPIResponse,
  UseCropRecommendationsOptions,
  UseCropRecommendationsReturn,
} from '../types'
import type { GeographicContext } from '../types/geocoding'

/**
 * AGC: [CROP_REC] Calculate centroid from polygon coordinates
 * @param coordinates - Array of [lat, lng] pairs
 * @returns Centroid { lat, lng } or null
 */
const calculateCentroid = (
  coordinates: [number, number][] | null
): { lat: number; lng: number } | null => {
  if (!coordinates || coordinates.length === 0) {
    return null
  }

  let sumLat = 0
  let sumLng = 0

  for (const [lat, lng] of coordinates) {
    sumLat += lat
    sumLng += lng
  }

  return {
    lat: sumLat / coordinates.length,
    lng: sumLng / coordinates.length,
  }
}

/**
 * AGC: [CROP_REC] Fetch recommendations from backend API
 * @param coordinates - Lot polygon coordinates
 * @param plantingMonth - Target planting month (1-12)
 * @param yearsBack - Years of historical data to analyze
 * @param lotId - Optional lot ID for reference
 * @param fieldId - Optional field ID for audit logging
 * @param userId - Optional user ID for audit logging
 * @param token - Optional auth token for API authentication
 * @param flowRunId - Optional flow run ID for tracing
 * @returns Recommendation API response
 */
const fetchRecommendations = async (
  coordinates: [number, number][],
  plantingMonth?: number,
  yearsBack?: number,
  lotId?: string,
  fieldId?: string,
  userId?: string,
  token?: string,
  flowRunId?: string,
  manejo?: string, // AGC: [REC_MANEJO] Default backend = 'sequeiro' if undefined
): Promise<RecommendationAPIResponse> => {
  const backendUrl = getBackendUrl()
  const url = `${backendUrl}/api/v1/recommendations`

  console.log(`🌱 AGC: [CROP_REC] Fetching recommendations from ${url}`)
  console.log(`🌱 AGC: [CROP_REC] Coordinates: ${coordinates.length} points`)
  console.log(`🌱 AGC: [CROP_REC] Planting month: ${plantingMonth || 'current'}`)
  console.log(`🌱 AGC: [CROP_REC] Lot ID: ${lotId || 'N/A'}, Field ID: ${fieldId || 'N/A'}`)

  // AGC: [AUDIT] Build headers with auth info
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  if (userId) {
    headers['x-user-id'] = userId
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      lotId,
      coordinates,
      plantingMonth,
      yearsBack: yearsBack || 5,
      fieldId, // AGC: [AUDIT] For computation trace
      flowRunId, // AGC: [FLOW_TRACE] For tracing
      manejo, // AGC: [REC_MANEJO] If undefined, backend defaults to 'sequeiro'
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.message || `API error: ${response.status}`)
  }

  const result: RecommendationAPIResponse = await response.json()

  if (!result.success) {
    throw new Error(result.message || 'Recommendation API returned unsuccessful response')
  }

  return result
}

/**
 * AGC: [CROP_REC] useCropRecommendations - Main hook for geo + climate aware crop recommendations
 * 
 * @param options - Hook configuration
 * @param options.lotId - Lot ID for reference
 * @param options.coordinates - Lot polygon coordinates [[lat, lng], ...]
 * @param options.plantingMonth - Target planting month (1-12), defaults to current
 * @param options.yearsBack - Years of historical data to analyze (default: 5)
 * @param options.enabled - Whether to fetch (default: true if coordinates provided)
 * 
 * @returns Recommendations data and controls
 * 
 * @example
 * ```tsx
 * const { recommendations, isLoading, error } = useCropRecommendations({
 *   lotId: 'abc123',
 *   coordinates: plotData.coordinates,
 *   plantingMonth: 10,
 * })
 * ```
 */
export const useCropRecommendations = (
  options: UseCropRecommendationsOptions
): UseCropRecommendationsReturn => {
  const {
    lotId,
    fieldId, // AGC: [AUDIT] Field ID for audit logging
    coordinates,
    plantingMonth,
    yearsBack = 5,
    enabled = true,
    flowRunId, // AGC: [FLOW_TRACE] Flow run ID for tracing
    manejo, // AGC: [REC_MANEJO] Sequeiro or irrigado
  } = options

  // AGC: [AUDIT] Get auth context for user ID and token
  const { user, getIdToken } = useAuth()

  // AGC: [CROP_REC] State
  const [recommendations, setRecommendations] = useState<CropRecommendation[]>([])
  const [locationClimate, setLocationClimate] = useState<ClimateProfile | null>(null)
  const [targetMonth, setTargetMonth] = useState<number>(plantingMonth || new Date().getMonth() + 1)
  const [yearsAnalyzed, setYearsAnalyzed] = useState<number>(0)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [geographicContext, setGeographicContext] = useState<GeographicContext | null>(null)
  const [excludedCrops, setExcludedCrops] = useState<string[]>([])
  // AGC: [AR] Argentina superset fields (undefined when the centroid is not in Argentina).
  const [provider, setProvider] = useState<string | undefined>(undefined)
  const [zone, setZone] = useState<{ id: string; name: string } | null | undefined>(undefined)
  const [incomplete, setIncomplete] = useState<boolean | undefined>(undefined)
  const [effectivePlantingMonth, setEffectivePlantingMonth] = useState<number | undefined>(undefined)
  const [defaultPlantingMonth, setDefaultPlantingMonth] = useState<number | undefined>(undefined)
  const [coveredCount, setCoveredCount] = useState<number | undefined>(undefined)
  const [catalogTotal, setCatalogTotal] = useState<number | undefined>(undefined)

  // AGC: [CROP_REC] Refs to track mounted state and prevent stale closures
  const isMountedRef = useRef<boolean>(true)
  const abortControllerRef = useRef<AbortController | null>(null)

  // AGC: [CROP_REC] Check if we have valid coordinates
  const hasValidCoordinates = coordinates && Array.isArray(coordinates) && coordinates.length >= 3

  /**
   * AGC: [CROP_REC] Fetch recommendations from API
   */
  const fetchData = useCallback(async () => {
    // AGC: [CROP_REC] Validate coordinates
    if (!hasValidCoordinates) {
      console.warn('⚠️ AGC: [CROP_REC] No valid coordinates provided')
      setError('Coordenadas do lote não disponíveis')
      return
    }

    // AGC: [CROP_REC] Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    setError(null)

    // AGC: [CROP_REC] Log centroid for debugging
    const centroid = calculateCentroid(coordinates)
    console.log(`📍 AGC: [CROP_REC] Lot centroid: (${centroid?.lat.toFixed(4)}, ${centroid?.lng.toFixed(4)})`)

    // AGC: [AUDIT] Get auth token for API call
    let token: string | undefined
    try {
      token = await getIdToken?.()
    } catch {
      console.warn('⚠️ AGC: [CROP_REC] Could not get auth token')
    }

    try {
      const result = await fetchRecommendations(
        coordinates!,
        plantingMonth,
        yearsBack,
        lotId,
        fieldId, // AGC: [AUDIT] For computation trace
        user?.uid, // AGC: [AUDIT] For computation trace
        token, // AGC: [AUDIT] For API auth
        flowRunId, // AGC: [FLOW_TRACE] For tracing
        manejo, // AGC: [REC_MANEJO]
      )

      // AGC: [CROP_REC] Only update state if still mounted
      if (!isMountedRef.current) return

      if (result.success && result.data) {
        console.log(`✅ AGC: [CROP_REC] Received ${result.data.recommendations.length} recommendations`)

        setRecommendations(result.data.recommendations)
        setLocationClimate(result.data.locationClimate)
        // AGC: [AR] AR payload has effective_planting_month (no targetMonth) — seed the picker from it.
        setTargetMonth(result.data.targetMonth ?? result.data.effective_planting_month ?? (new Date().getMonth() + 1))
        setYearsAnalyzed(result.data.yearsAnalyzed ?? 0) // AGC: [AR] AR payload has no yearsAnalyzed
        setGeographicContext(result.data.geographicContext || null)
        setExcludedCrops(result.data.excludedCrops || [])
        // AGC: [AR] surface Argentina superset fields when present (undefined for Brazil).
        setProvider(result.data.provider)
        setZone(result.data.zone ?? null)
        setIncomplete(result.data.incomplete)
        setEffectivePlantingMonth(result.data.effective_planting_month ?? undefined)
        setDefaultPlantingMonth(result.data.default_planting_month ?? undefined)
        // AGC: [AR] catalog coverage counts (undefined for Brazil).
        setCoveredCount(result.data.covered_count ?? undefined)
        setCatalogTotal(result.data.catalog_total ?? undefined)

        // AGC: [CROP_REC] Log top recommendations
        const top3 = result.data.recommendations.slice(0, 3)
        console.log(`🏆 AGC: [CROP_REC] Top 3: ${top3.map(r => `${r.name} (${r.suitabilityScore})`).join(', ')}`)
      } else {
        throw new Error(result.message || 'Failed to get recommendations')
      }

    } catch (err) {
      if (!isMountedRef.current) return

      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar recomendações'
      console.error(`❌ AGC: [CROP_REC] Error:`, errorMessage)
      setError(errorMessage)

    } finally {
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [coordinates, plantingMonth, yearsBack, lotId, fieldId, hasValidCoordinates, user?.uid, getIdToken, manejo])

  /**
   * AGC: [CROP_REC] Refetch recommendations (exposed to component)
   */
  const refetch = useCallback(async () => {
    await fetchData()
  }, [fetchData])

  // AGC: [CROP_REC] Effect: Fetch on mount and when dependencies change
  useEffect(() => {
    isMountedRef.current = true

    // AGC: [CROP_REC] Only fetch if enabled and we have coordinates
    if (enabled && hasValidCoordinates) {
      fetchData()
    }

    return () => {
      isMountedRef.current = false
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [enabled, hasValidCoordinates, fetchData])

  // AGC: [CROP_REC] Return hook value
  return {
    recommendations,
    locationClimate,
    targetMonth,
    yearsAnalyzed,
    isLoading,
    error,
    refetch,
    geographicContext,
    excludedCrops,
    provider,
    zone,
    incomplete,
    effectivePlantingMonth,
    defaultPlantingMonth,
    coveredCount,
    catalogTotal,
  }
}

// =============================================================================
// AGC: [CROP_REC] UTILITY HOOK FOR SIMPLE COORDINATE-BASED LOOKUP
// =============================================================================

/**
 * AGC: [CROP_REC] Simple hook for fetching recommendations by coordinates only
 * AGC: Useful for quick lookups without full lot data
 * 
 * @param lat - Latitude
 * @param lng - Longitude
 * @param month - Target planting month (optional)
 * @returns Recommendations data
 */
export const useRecommendationsByLocation = (
  lat: number | null,
  lng: number | null,
  month?: number
): UseCropRecommendationsReturn => {
  // AGC: [CROP_REC] Create synthetic polygon from single point
  const coordinates: [number, number][] | null = (lat !== null && lng !== null)
    ? [
        [lat - 0.001, lng - 0.001],
        [lat + 0.001, lng - 0.001],
        [lat + 0.001, lng + 0.001],
        [lat - 0.001, lng + 0.001],
      ]
    : null

  return useCropRecommendations({
    coordinates,
    plantingMonth: month,
    enabled: lat !== null && lng !== null,
  })
}

export default useCropRecommendations

